import { Page, pickSeoProps } from "@components/page/Page"

import { Navigation } from "@generated/models/Navigation"
import { Footer } from "@generated/models/Footer"
import {
  fetchContent,
  MultipleItemResponse,
  SingleItemResponse,
} from "@lib/kontent"
import { GetStaticPaths, GetStaticProps, NextPage } from "next"
import React from "react"
import { RichContentPage as RichContentPageModel } from "../__generated__/models/RichContentPage"
import { Blocks } from "@components/ui/blocks"
import { Container, CONTENT_BLOCK_MB } from "@components/ui/Container"
import { Heading } from "@components/ui/typograhpy/Heading"

type RichContentPageProps = {
  navigation: SingleItemResponse<Navigation>
  footer: SingleItemResponse<Footer>
  content: RichContentPageModel
  modular_content: { [key: string]: any }
}

const RichContentPage: NextPage<RichContentPageProps> = ({
  content,
  navigation,
  footer,
  modular_content,
}) => {
  return (
    <Page navigation={navigation} footer={footer} seo={pickSeoProps(content)}>
      {content.title.value && (
        <Container mb={CONTENT_BLOCK_MB} mt={CONTENT_BLOCK_MB}>
          <Heading as="h1" variant="h1">
            {content.title.value}
          </Heading>
        </Container>
      )}
      <Blocks content={content} modular_content={modular_content} />
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const pages = await fetchContent<MultipleItemResponse<RichContentPageModel>>({
    params: { "system.type": "rich_content_page" },
  })

  return {
    paths: pages.items.map((item) => ({
      params: {
        slug:
          item.elements.url_slug.value === "/"
            ? ["/"]
            : item.elements.url_slug.value.split("/"),
      },
    })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  if (typeof params.slug !== "object") {
    return { notFound: true }
  }
  const navigation = await fetchContent<SingleItemResponse<Navigation>>({
    path: "/navigation",
    preview,
  })

  const footer = await fetchContent<SingleItemResponse<Footer>>({
    path: "/footer",
    preview,
  })

  const content = await fetchContent<
    MultipleItemResponse<RichContentPageModel>
  >({
    preview,
    params: {
      "system.type": "rich_content_page",
      "elements.url_slug": params.slug.join("/"),
      depth: 3,
    },
  })
  if (!content.items[0]) {
    return { notFound: true }
  }
  return {
    props: {
      navigation,
      footer,
      content: content.items[0].elements,
      modular_content: content.modular_content,
    },
  }
}

export default RichContentPage
